import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
    getMyArtists,
    getEvents,
    getActivityById,
    myArtistsWithActivities,
    myArtistsCalendarData,
    myArtistsEventsBasic,
    myArtistsActivitiesDetailed,
    getEventById,
    getArtistById,
    getToursByArtist,
    getTourById,
    myPendingEventInvites
} from "@firebasegen/default-connector";

import { useAuth } from "./AuthContext";
import { useImpersonation } from "./ImpersonationContext";
import { dataConnect } from "../firebaseConfig";
import Logger from '../utils/Logger';

// Export new hooks
export const useArtists = (options = {}) => {
    const { user, loading: authLoading } = useAuth();
    return useQuery({
        queryKey: ['artists'],
        queryFn: async () => {
            try {
                Logger.debug("ArtistContext", "Fetching artists...");
                const result = await getMyArtists(dataConnect);
                Logger.debug("ArtistContext", "Fetched artists successfully", { count: result.data.artists?.length });
                return result.data.artists;
            } catch (error) {
                Logger.error("ArtistContext", "Error fetching artists", error);
                throw error;
            }
        },
        enabled: !!user && !authLoading, // Only fetch if user is logged in and auth is ready
        staleTime: 1000 * 60 * 60, // 1 hour (Increased for offline simulation resilience)
        placeholderData: keepPreviousData,
        ...options,
    });
};

export const useActiveArtists = () => {
    const { user } = useAuth();
    const { getEffectiveUid } = useImpersonation();
    const { data: artists, isError, error, isLoading, ...rest } = useArtists();

    const activeArtists = React.useMemo(() => {
        if (!artists) return null;
        const effectiveUid = getEffectiveUid();
        return artists.filter(artist => {
            const myCrew = artist.crewMembers_on_artist?.find(cm => cm.user?.id === effectiveUid);
            return myCrew?.accepted === true;
        });
    }, [artists, getEffectiveUid]);

    return { ...rest, data: activeArtists, isError, error, isLoading };
};

export const useInvites = () => {
    const { user } = useAuth();
    const { getEffectiveUid } = useImpersonation();
    const { data: artists, ...rest } = useArtists();

    const invites = React.useMemo(() => {
        if (!artists) return null;
        const effectiveUid = getEffectiveUid();
        return artists.filter(artist => {
            const myCrew = artist.crewMembers_on_artist?.find(cm => cm.user?.id === effectiveUid);
            return myCrew && myCrew.accepted !== true;
        }).map(artist => {
            const effectiveUid2 = getEffectiveUid();
            const myCrew = artist.crewMembers_on_artist?.find(cm => cm.user?.id === effectiveUid2);
            return {
                id: myCrew?.id || artist.id,
                artistId: artist.id,
                artistName: artist.name,
                role: myCrew?.role,
            };
        });
    }, [artists, getEffectiveUid]);

    return { ...rest, data: invites };
};

const filterUpcomingPendingInvites = (eventCrews) => {
    const today = moment().startOf('day');
    return (eventCrews || [])
        .filter(invite => invite?.event?.date && moment(invite.event.date).isSameOrAfter(today, 'day'))
        .sort((a, b) => new Date(a.event.date) - new Date(b.event.date));
};

export const usePendingEventInvites = () => {
    const { user, loading: authLoading } = useAuth();
    return useQuery({
        queryKey: ['pendingEventInvites'],
        queryFn: async () => {
            const result = await myPendingEventInvites(dataConnect, { fetchPolicy: 'SERVER_ONLY' });
            return filterUpcomingPendingInvites(result.data.eventCrews);
        },
        select: filterUpcomingPendingInvites,
        enabled: !!user && !authLoading,
        staleTime: 1000 * 60, // 1 minute
    });
};

//with date range
export const useArtistsWithDateRange = (startDate, endDate) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['artists', { startDate, endDate }],
        queryFn: async () => {
            const result = await myArtistsWithActivities(dataConnect, {
                fromDate: startDate,
                toDate: endDate,
                fromDateTs: new Date(startDate).toISOString(),
                toDateTs: new Date(endDate).toISOString()
            });
            return result.data.artists;
        },
        enabled: !!user && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
    });
};

export const useArtistsCalendarData = (startDate, endDate) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['artists-calendar', { startDate, endDate }],
        queryFn: async () => {
            const result = await myArtistsCalendarData(dataConnect, {
                fromDate: startDate,
                toDate: endDate,
                fromDateTs: new Date(startDate).toISOString(),
                toDateTs: new Date(endDate).toISOString()
            });
            return result.data.artists;
        },
        enabled: !!user && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
    });
};

// Starting data for a date range that has no cache yet (e.g. first launch on a new day without coverage):
// the most recently fetched range of the same query. Used with initialDataUpdatedAt: 0 so it counts as
// outdated and is refetched right away, but stays on screen if that refetch fails.
const latestCachedRange = (queryClient, prefix) => queryClient.getQueryCache()
    .findAll({ queryKey: [prefix] })
    .filter(query => query.state.data !== undefined)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0]?.state.data;

export const useArtistsEventsBasic = (startDate, endDate) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: ['artists-events-basic', { startDate, endDate }],
        queryFn: async () => {
            const result = await myArtistsEventsBasic(dataConnect, {
                fromDate: startDate,
                toDate: endDate,
            });
            return result.data.artists;
        },
        enabled: !!user && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
        initialData: () => latestCachedRange(queryClient, 'artists-events-basic'),
        initialDataUpdatedAt: 0,
    });
};

export const useArtistsActivitiesDetailed = (startDate, endDate) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: ['artists-activities-detailed', { startDate, endDate }],
        queryFn: async () => {
            const result = await myArtistsActivitiesDetailed(dataConnect, {
                fromDateTs: new Date(startDate).toISOString(),
                toDateTs: new Date(endDate).toISOString()
            });
            return result.data.artists;
        },
        enabled: !!user && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
        initialData: () => latestCachedRange(queryClient, 'artists-activities-detailed'),
        initialDataUpdatedAt: 0,
    });
};

export const useArtistById = (id) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['artist', id],
        queryFn: async () => {
            const result = await getArtistById(dataConnect, { artistId: id });
            return result.data.artists[0]; // getArtistById returns an array with one item
        },
        enabled: !!user && !!id,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
    });
};

export const useEvents = (options = {}) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['events'],
        queryFn: async () => {
            try {
                Logger.debug("ArtistContext", "Fetching all events...");
                const result = await getEvents(dataConnect);
                Logger.debug("ArtistContext", "Fetched events successfully", { count: result.data.events?.length });
                return result.data.events;
            } catch (error) {
                Logger.error("ArtistContext", "Error fetching events", error);
                throw error;
            }
        },
        enabled: !!user,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
        ...options,
    });
};

export const useEventById = (id) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['event', id],
        queryFn: async () => {
            try {
                Logger.debug("ArtistContext", `Fetching event by ID: ${id}`);
                const result = await getEventById(dataConnect, { eventId: id });
                Logger.debug("ArtistContext", `Fetched event successfully: ${id}`);
                return result.data.events[0]; // getEventById returns an array with one item
            } catch (error) {
                Logger.error("ArtistContext", `Error fetching event: ${id}`, error);
                throw error;
            }
        },
        enabled: !!user && !!id,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
    });
};

export const useActivityByIdOptimized = (id) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['activity', id],
        queryFn: async () => {
            try {
                Logger.debug("ArtistContext", `Fetching activity by ID: ${id}`);
                const result = await getActivityById(dataConnect, { activityId: id });
                Logger.debug("ArtistContext", `Fetched activity successfully: ${id}`);
                return result.data.activities[0]; // getActivityById returns an array with one item
            } catch (error) {
                Logger.error("ArtistContext", `Error fetching activity: ${id}`, error);
                throw error;
            }
        },
        enabled: !!user && !!id,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
    });
};

export const useTours = (artistId, options = {}) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['tours', artistId],
        queryFn: async () => {
            try {
                Logger.debug("ArtistContext", `Fetching tours for artist: ${artistId}`);
                const result = await getToursByArtist(dataConnect, { artistId });
                Logger.debug("ArtistContext", `Fetched tours successfully for artist: ${artistId}`, { count: result.data.tours?.length });
                return result.data.tours;
            } catch (error) {
                Logger.error("ArtistContext", `Error fetching tours for artist: ${artistId}`, error);
                throw error;
            }
        },
        enabled: !!user && !!artistId,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
        ...options,
    });
};

export const useTourById = (tourId) => {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['tour', tourId],
        queryFn: async () => {
            try {
                Logger.debug("ArtistContext", `Fetching tour by ID: ${tourId}`);
                const result = await getTourById(dataConnect, { tourId });
                Logger.debug("ArtistContext", `Fetched tour successfully: ${tourId}`);
                return result.data.tours[0];
            } catch (error) {
                Logger.error("ArtistContext", `Error fetching tour: ${tourId}`, error);
                throw error;
            }
        },
        enabled: !!user && !!tourId,
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: keepPreviousData,
    });
};

